/**
 * Pre-visit patient brief ("clinical memory", owner meeting 2026-10-27).
 *
 * After the morning huddle each hygienist, doctor and assistant needs a
 * quick per-patient summary of what to consider and talk about: unfinished
 * work, interests the patient once mentioned, home care, how last visit's
 * problem feels now, alerts and the grade. NOT new diagnosis.
 *
 * - Alerts, the grade, the last visit and the unfinished plan lines are read
 *   straight from the record (never written by a model).
 * - The talking points are written by the `patient.brief` AI lane (OpenAI
 *   under the BAA only) from the patient's notes, visits, plan, staff notes
 *   and analysed calls, and every point carries the sources it came from.
 *   Points that cite nothing, or mention a tooth, code or year their sources
 *   do not contain, are dropped before they are stored.
 * - A worker pre-generates briefs for tomorrow's patients each evening and
 *   today's early in the morning, and regenerates one when new notes sync.
 *
 * Routes live under `/v1/patient-brief`.
 */
import { z } from 'zod';

export const BRIEF_PERMISSIONS = Object.freeze({
  /** Read a brief (clinical content): the clinical team. */
  view: 'chart.view',
});

/** Live-refresh entity key: briefs refetch when one is (re)generated. */
export const BRIEF_ENTITY = 'patient_brief';

/** The talking-point sections, in display order. */
export const BRIEF_SECTION_KEYS = [
  'follow_up',
  'unfinished',
  'interests',
  'home_care',
  'comfort',
  'watch',
] as const;
export type BriefSectionKey = (typeof BRIEF_SECTION_KEYS)[number];

export const BRIEF_SECTION_TITLE: Readonly<Record<BriefSectionKey, string>> = Object.freeze({
  follow_up: 'Follow up from last time',
  unfinished: 'Unfinished treatment',
  interests: 'Interests mentioned',
  home_care: 'Home care',
  comfort: 'Comfort and preferences',
  watch: 'Keep an eye on',
});

export const BRIEF_SOURCE_KINDS = [
  'note',
  'visit',
  'plan',
  'staff_note',
  'commlog',
  'call',
  'appointment_note',
] as const;
export type BriefSourceKind = (typeof BRIEF_SOURCE_KINDS)[number];

export const BriefSourceSchema = z.object({
  kind: z.enum(BRIEF_SOURCE_KINDS),
  /** The record's id (note, appointment, plan line, call...), when it has one. */
  id: z.string().nullable(),
  /** YYYY-MM-DD. */
  date: z.string().nullable(),
  /** "Clinical note, Dr. Moody", "Visit: D0140 limited exam". No patient text. */
  label: z.string().max(160),
});
export type BriefSource = z.infer<typeof BriefSourceSchema>;

export const BriefPointSchema = z.object({
  text: z.string().min(1).max(240),
  sources: z.array(BriefSourceSchema).min(1).max(6),
});
export type BriefPoint = z.infer<typeof BriefPointSchema>;

export const BriefSectionSchema = z.object({
  key: z.enum(BRIEF_SECTION_KEYS),
  title: z.string(),
  points: z.array(BriefPointSchema).max(4),
});
export type BriefSection = z.infer<typeof BriefSectionSchema>;

export const TALKING_POINT_BENEFITS = ['patient', 'office', 'both'] as const;

export const TalkingPointSchema = BriefPointSchema.extend({
  /** Who it helps: the patient's health, the office (production), or both. */
  benefit: z.enum(TALKING_POINT_BENEFITS),
});
export type TalkingPoint = z.infer<typeof TalkingPointSchema>;

export const BriefAlertSchema = z.object({
  kind: z.string(),
  text: z.string(),
  severity: z.enum(['info', 'warning', 'critical']),
});

export const BriefGradeSchema = z.object({
  letter: z.enum(['A', 'B', 'C', 'D', 'F']),
  word: z.string(),
  provisional: z.boolean(),
  /** The biggest reason, e.g. "2 no-shows in 12 months". */
  reason: z.string().nullable(),
});

export const BriefLastVisitSchema = z.object({
  date: z.string(),
  /** "Limited exam, PA x-ray" (descriptions of the completed codes). */
  what: z.string(),
  /** A limited / emergency exam or palliative visit (D0140, D0170, D9110). */
  emergency: z.boolean(),
  provider: z.string().nullable(),
});

export const BriefOutstandingSchema = z.object({
  code: z.string(),
  description: z.string(),
  tooth: z.string().nullable(),
  /** YYYY-MM-DD planned. */
  plannedOn: z.string().nullable(),
  feeCents: z.number().int().nullable(),
});

export const BRIEF_STATUSES = [
  /** Generated and current. */
  'ready',
  /** Queued or generating. */
  'pending',
  /** The model call failed; the record facts still show. */
  'failed',
  /** No AI for this practice (not on the BAA list) or no lane configured. */
  'unavailable',
  /** Not generated yet. */
  'none',
] as const;
export type BriefStatus = (typeof BRIEF_STATUSES)[number];

export const PatientBriefSchema = z.object({
  patientId: z.string().uuid(),
  status: z.enum(BRIEF_STATUSES),
  /** New notes or visits synced since it was written (a refresh is due). */
  stale: z.boolean(),
  generatedAt: z.string().nullable(),
  model: z.string().nullable(),
  /** Why the brief is failed or unavailable (no PHI). */
  note: z.string().nullable(),
  /** One line, e.g. "Back for the crown on #19; ask how the toothache is." */
  headline: z.string().nullable(),
  /** From the record, never a model. */
  alerts: z.array(BriefAlertSchema),
  grade: BriefGradeSchema.nullable(),
  lastVisit: BriefLastVisitSchema.nullable(),
  outstanding: z.array(BriefOutstandingSchema),
  outstandingTotalCents: z.number().int(),
  /** From the model, every point sourced. */
  sections: z.array(BriefSectionSchema),
  talkingPoints: z.array(TalkingPointSchema),
  /** How much history the brief read. */
  coverage: z.object({
    notes: z.number().int(),
    visits: z.number().int(),
    calls: z.number().int(),
    firstVisit: z.string().nullable(),
  }),
});
export type PatientBrief = z.infer<typeof PatientBriefSchema>;

/** The schedule hover's short form (batched for a day's patients). */
export const BriefSummarySchema = z.object({
  patientId: z.string().uuid(),
  status: z.enum(BRIEF_STATUSES),
  headline: z.string().nullable(),
  /** Up to 3 short lines: the most useful points first. */
  lines: z.array(z.string()).max(3),
});
export type BriefSummary = z.infer<typeof BriefSummarySchema>;

export const BriefSummaryQuerySchema = z.object({
  /** Comma-separated patient ids (at most 200). */
  patientIds: z
    .string()
    .min(1)
    .max(200 * 37),
});
export const BriefSummaryListSchema = z.object({ briefs: z.array(BriefSummarySchema) });

export const BriefPatientParamsSchema = z.object({ patientId: z.string().uuid() });

export const BriefDayQuerySchema = z.object({
  /** YYYY-MM-DD in the office's time zone; default today. */
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  /** One provider (doctor, hygienist or assistant on the visit); default everyone. */
  providerId: z.string().uuid().optional(),
});

export const BriefDayVisitSchema = z.object({
  appointmentId: z.string().uuid(),
  patientId: z.string().uuid(),
  patientName: z.string(),
  startsAt: z.string(),
  lengthMin: z.number().int(),
  operatory: z.string().nullable(),
  provider: z.string().nullable(),
  providerId: z.string().uuid().nullable(),
  hygienistId: z.string().uuid().nullable(),
  type: z.string().nullable(),
  brief: PatientBriefSchema,
});
export type BriefDayVisit = z.infer<typeof BriefDayVisitSchema>;

export const BriefDayProviderSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  kind: z.string().nullable(),
  visits: z.number().int(),
});

export const BriefDaySchema = z.object({
  date: z.string(),
  locationId: z.string().uuid(),
  /** The office's IANA time zone (visit times are instants). */
  timezone: z.string(),
  /** The signed-in user's provider id, when they are one (the page's default filter). */
  myProviderId: z.string().uuid().nullable(),
  providers: z.array(BriefDayProviderSchema),
  visits: z.array(BriefDayVisitSchema),
});
export type BriefDay = z.infer<typeof BriefDaySchema>;

export const BriefGenerateResultSchema = z.object({ brief: PatientBriefSchema });
